import { parseISO } from "date-fns";

import { RANGE_FILTER } from "constants/Constants";
import {
  operandsOf,
  readEnvelope,
} from "components/NewAdvancedSearch/envelope";
import {
  isNoneOptionId,
  NONE_OPTION,
  NONE_OPTION_ID,
} from "components/NewAdvancedSearch/filters/noneOption";
import { isPresenceOp } from "components/NewAdvancedSearch/filters/selectOperators";

// Rebuilds the new search's rules from the URL query params — the inverse of
// buildQueryParams. Async because resolving an async select's ids back into
// full option objects requires calling its queryFn.
//
// Reads both wire formats. The standardized { op, value } envelope is what the
// panel emits now; the suffixed params and bare CSVs are what a bookmarked URL
// or a saved filter still carries, and those keep working unchanged.

const STRING_ID_KEYS = ["caller_id", "contact_number", "machine_id"];

// Turn a wire operator back into the RANGE_FILTER op the range/date editors
// expect. Both maps carry the operator names a saved filter may still hold
// alongside the canonical ones.
const RANGE_BY_NUMERIC_OP = {
  eq: RANGE_FILTER.EQUALS,
  gte: RANGE_FILTER.GREATER_THAN,
  lte: RANGE_FILTER.LESS_THAN,
  between: RANGE_FILTER.BETWEEN,
};

const RANGE_BY_DATE_OP = {
  eq: RANGE_FILTER.EQUALS,
  gt: RANGE_FILTER.GREATER_THAN,
  lt: RANGE_FILTER.LESS_THAN,
  between: RANGE_FILTER.BETWEEN,
  exactly: RANGE_FILTER.EQUALS,
  after: RANGE_FILTER.GREATER_THAN,
  before: RANGE_FILTER.LESS_THAN,
};

const safeJsonParse = raw => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const boundsFromEnvelope = (raw, opsByName, deserialize) => {
  const clauses = readEnvelope(raw);
  if (!clauses?.length) return null;
  const [clause] = clauses;
  const op = opsByName[clause.op];
  if (op == null) return null;
  return { op, value: operandsOf(clause).map(deserialize) };
};

const castId = (id, key) => {
  if (id === "true" || id === "false") return id === "true";
  if (id !== "" && !isNaN(+id) && !STRING_ID_KEYS.includes(key)) return +id;
  return id;
};

// Only a filter that offers the None option can carry the sentinel in its URL,
// so only there is an id read as one. Elsewhere an id that merely looks like the
// sentinel is a real value: a custom table's choices and list elements are tenant
// authored and are their own ids, so "None" can be one of them, and its filter
// has no None option to confuse it with. Within a filter that does offer the
// option, a resolved option still wins. Resolution walks the ids in the order the
// URL lists them, so a reloaded panel shows the same chips in the same places
// rather than reordering by the server's response.
const resolveSelectObjects = async (descriptor, ids) => {
  const offersNoneOption = !!descriptor.props?.includeNoneOption;
  const isSentinel = id => offersNoneOption && isNoneOptionId(id);
  const byId = new Map();

  if (descriptor.data?.length) {
    descriptor.data.forEach(o => byId.set(o.id, o));
  } else if (descriptor.queryFn) {
    // The sentinel has no record behind it, so it stays out of the lookup --
    // asking the server for an id that cannot exist is what answered 422.
    const realIds = ids.filter(id => !isSentinel(id));
    if (realIds.length) {
      const data = await descriptor.queryFn({ model_id: realIds });
      data.forEach(o =>
        byId.set(o.id, { ...o, name: o.name ?? o.full_name ?? o.text ?? "" })
      );
    }
  }

  return ids
    .map(id => byId.get(id) ?? (isSentinel(id) ? NONE_OPTION : undefined))
    .filter(Boolean);
};

const parseRange = (key, qp) => {
  const fromEnvelope = boundsFromEnvelope(qp[key], RANGE_BY_NUMERIC_OP, Number);
  if (fromEnvelope) return fromEnvelope;

  const type = qp[`${key}:type`];
  const start = qp[`${key}:start`];
  const end = qp[`${key}:end`];
  if (type == null && start == null && end == null) return null;
  const value = [];
  if (start != null) value[0] = start;
  if (end != null) value[1] = end;
  return { op: type != null ? Number(type) : RANGE_FILTER.GREATER_THAN, value };
};

const parseDate = (key, qp) => {
  const fromEnvelope = boundsFromEnvelope(qp[key], RANGE_BY_DATE_OP, parseISO);
  if (fromEnvelope) return fromEnvelope;

  const from = qp[`${key}:from`];
  const to = qp[`${key}:to`];
  if (from == null && to == null) return null;
  if (from != null && to != null) {
    // A single-day range is the serialized form of Equals; anything wider is a
    // genuine Between.
    if (from === to) {
      return { op: RANGE_FILTER.EQUALS, value: [parseISO(from)] };
    }
    return { op: RANGE_FILTER.BETWEEN, value: [parseISO(from), parseISO(to)] };
  }
  if (from != null) {
    return { op: RANGE_FILTER.GREATER_THAN, value: [parseISO(from)] };
  }
  return { op: RANGE_FILTER.LESS_THAN, value: [parseISO(to)] };
};

// The operator is kept as the param carries it: reading `not_in` or a presence
// operator back as `in` would show a different filter than the one applied,
// and re-applying the panel would write that weaker filter back.
const parseSelect = async (descriptor, raw) => {
  if (raw == null) return null;
  let list;
  let negated = false;
  const clauses = readEnvelope(raw);
  if (clauses?.length && isPresenceOp(clauses[0].op)) {
    return { op: clauses[0].op, value: descriptor.isSingle ? null : [] };
  }
  if (clauses?.length) {
    negated = clauses[0].op === "not_in";
    // A JSON null is the unset sentinel; the option list spells it `Null`.
    list = operandsOf(clauses[0]).map(id =>
      id === null ? NONE_OPTION_ID : id
    );
  } else if (descriptor.json) {
    list = safeJsonParse(raw);
    if (!Array.isArray(list)) return null;
  } else {
    list = String(raw).split(",");
  }
  const ids = list.map(id => castId(id, descriptor.key));
  const objects = await resolveSelectObjects(descriptor, ids);
  if (descriptor.isSingle) {
    return { op: negated ? "not_in" : "is", value: objects[0] ?? null };
  }
  return { op: negated ? "not_in" : "in", value: objects };
};

// A multi-value custom field's match mode rides its operator: `all` matches
// every value, `in` matches any. A legacy value has no operator, so the mode
// comes from the table-wide or_filters param instead — resolved by the caller.
const categoricalValues = raw => {
  const clauses = readEnvelope(raw);
  if (clauses?.length) {
    return {
      values: operandsOf(clauses[0]),
      matchAll: clauses[0].op === "all",
    };
  }
  return {
    values: String(raw).split(",").filter(Boolean),
    matchAll: undefined,
  };
};

const parseCustomField = (descriptor, raw) => {
  if (raw == null) return null;
  switch (descriptor.type) {
    case "select": {
      const options = descriptor.options?.options ?? [];
      const isSingle = descriptor.options?.type !== "multi";
      const { values, matchAll } = categoricalValues(raw);
      const objects = values
        .map(id => options.find(o => o.id === id))
        .filter(Boolean);
      const value = isSingle
        ? { op: "is", value: objects[0] ?? null }
        : { op: "in", value: objects };
      return matchAll === undefined ? value : { ...value, matchAll };
    }
    case "list": {
      const { values, matchAll } = categoricalValues(raw);
      return matchAll === undefined
        ? { value: values }
        : { value: values, matchAll };
    }
    case "numeric":
      return boundsFromEnvelope(raw, RANGE_BY_NUMERIC_OP, Number);
    case "date":
      return boundsFromEnvelope(raw, RANGE_BY_DATE_OP, parseISO);
    case "boolean":
      return { op: "is", value: String(raw) === "true" };
    default:
      return { op: "contains", value: raw };
  }
};

const parseGrid = (descriptor, qp) => {
  const selected = descriptor.components.filter(
    c => String(qp[c.key]) === "true"
  );
  return selected.length
    ? { op: "in", value: selected.map(c => ({ id: c.key, name: c.title })) }
    : null;
};

// A listFilter reads its envelope off the bare key. A legacy search carries it
// under `search:<key>:<uuid>`; multi-group legacy searches (uuid 1+) collapse
// to the first group, which the single-row UI can represent.
const parseListFilter = (descriptor, qp) => {
  const clauses = readEnvelope(qp[descriptor.key]);
  if (clauses?.length) {
    const [clause] = clauses;
    const values = operandsOf(clause);
    return values.length
      ? { op: clause.op === "not_in" ? "excludes" : "contains", value: values }
      : null;
  }

  const prefix = `search:${descriptor.key}:`;
  const entry = Object.keys(qp).find(k => k.startsWith(prefix));
  if (!entry) return null;
  try {
    const { operationType, values } = JSON.parse(qp[entry]);
    return values?.length ? { op: operationType, value: values } : null;
  } catch {
    return null;
  }
};

const parseRegular = (descriptor, qp) => {
  const { key, type } = descriptor;
  switch (type) {
    case "grid":
      return parseGrid(descriptor, qp);
    case "listFilter":
      return parseListFilter(descriptor, qp);
    case "range":
      return parseRange(key, qp);
    case "date":
    case "dateRange":
      return parseDate(key, qp);
    case "select":
    case "ruvixxSelect":
      return parseSelect(descriptor, qp[key]);
    case "ruvixxToggle":
      return qp[key] == null
        ? null
        : { op: "is", value: String(qp[key]) === "true" };
    default:
      return qp[key] == null ? null : { op: "contains", value: qp[key] };
  }
};

const toTagIds = values => values.map(Number).filter(n => !Number.isNaN(n));

const parseTagGroups = raw => {
  const clauses = readEnvelope(raw);
  if (clauses) {
    return clauses
      .map(clause => ({
        op: clause.op === "not_in" ? "excludes" : "contains",
        value: toTagIds(operandsOf(clause)),
      }))
      .filter(group => group.value.length);
  }

  return String(raw)
    .split(";")
    .filter(Boolean)
    .map(segment => {
      const excludes = segment.startsWith("-");
      return {
        op: excludes ? "excludes" : "contains",
        value: toTagIds((excludes ? segment.slice(1) : segment).split(",")),
      };
    })
    .filter(group => group.value.length);
};

export const parseQueryParams = async (queryParams, filters) => {
  const tasks = [];

  // Tags live in a single `tags` param: a clause array now, and before that a
  // `;`-joined string of `-`-prefixed id groups. Either way each group becomes
  // its own rule.
  const tagDescriptor = filters.find(f => f.type === "tag");
  if (tagDescriptor && queryParams.tags) {
    parseTagGroups(queryParams.tags).forEach(value => {
      tasks.push(
        Promise.resolve({ field: tagDescriptor, kind: "filter", value })
      );
    });
  }

  // The backend's `or_filters` param is a JSON list of the custom-field labels
  // whose multi-select values are OR'd (match any). Everything absent is AND
  // (match all), so restore each multi-select's toggle from membership.
  const orFilters = safeJsonParse(queryParams.or_filters) ?? [];
  const carriesMatchMode = descriptor =>
    descriptor.isCustomField &&
    (descriptor.type === "list" ||
      (descriptor.type === "select" && descriptor.options?.type === "multi"));

  filters.forEach(descriptor => {
    if (descriptor.type === "tag") return;
    const kind = descriptor.isCustomField ? "customField" : "filter";
    let value = descriptor.isCustomField
      ? parseCustomField(descriptor, queryParams[descriptor.key])
      : parseRegular(descriptor, queryParams);
    // An envelope carries the mode in its operator; only a legacy value needs
    // it recovered from the table-wide param.
    if (
      value != null &&
      value.matchAll === undefined &&
      carriesMatchMode(descriptor)
    ) {
      value = { ...value, matchAll: !orFilters.includes(descriptor.title) };
    }
    tasks.push(
      Promise.resolve(value).then(resolved =>
        resolved == null ? null : { field: descriptor, kind, value: resolved }
      )
    );
  });

  return (await Promise.all(tasks)).filter(Boolean);
};
